import Anser from "anser";
import * as React from "react";
import { cn } from "../lib/cn";
import { formatAbsolute, formatLogTime, formatRelative } from "../lib/format";
import type { LogLine, LogMatch } from "../lib/logs";

// ANSI colors map onto the terminal tokens, which are AA on surface.sunken in both themes (ui-spec §3.4.4).
const ANSI_INDEX: Record<string, number> = {
  "ansi-black": 0,
  "ansi-red": 1,
  "ansi-green": 2,
  "ansi-yellow": 3,
  "ansi-blue": 4,
  "ansi-magenta": 5,
  "ansi-cyan": 6,
  "ansi-white": 7,
  "ansi-bright-black": 8,
  "ansi-bright-red": 9,
  "ansi-bright-green": 10,
  "ansi-bright-yellow": 11,
  "ansi-bright-blue": 12,
  "ansi-bright-magenta": 13,
  "ansi-bright-cyan": 14,
  "ansi-bright-white": 15,
};

interface Segment {
  text: string;
  color?: string;
  bold?: boolean;
  match?: "match" | "current";
  tint?: boolean;
}

function ansiSegments(text: string): Segment[] {
  if (!text.includes("\u001b[")) return [{ text }];
  return Anser.ansiToJson(text, { use_classes: true, remove_empty: true }).map((p) => {
    const idx = p.fg ? ANSI_INDEX[p.fg] : undefined;
    return {
      text: p.content,
      ...(idx !== undefined ? { color: `var(--nd-term-ansi-${idx})` } : {}),
      ...(p.decorations?.includes("bold") ? { bold: true } : {}),
    };
  });
}

/** Splits segments at [start, end) so a range can be styled without losing ANSI colors. */
function mark(segments: Segment[], ranges: { start: number; end: number; kind: Segment["match"] | "tint" }[]): Segment[] {
  if (!ranges.length) return segments;
  const out: Segment[] = [];
  let offset = 0;
  for (const seg of segments) {
    const cuts = new Set<number>([0, seg.text.length]);
    for (const r of ranges) {
      if (r.start > offset && r.start < offset + seg.text.length) cuts.add(r.start - offset);
      if (r.end > offset && r.end < offset + seg.text.length) cuts.add(r.end - offset);
    }
    const points = [...cuts].sort((a, b) => a - b);
    for (let i = 0; i < points.length - 1; i++) {
      const a = points[i]!;
      const b = points[i + 1]!;
      const abs = offset + a;
      const hit = ranges.find((r) => abs >= r.start && abs < r.end);
      const piece: Segment = { ...seg, text: seg.text.slice(a, b) };
      if (hit?.kind === "tint") piece.tint = true;
      else if (hit?.kind) piece.match = hit.kind;
      out.push(piece);
    }
    offset += seg.text.length;
  }
  return out;
}

export type TimestampMode = "relative" | "absolute" | "off";

export function LogLineView({
  line,
  matches,
  currentMatch,
  highlight,
  timestamps,
  now,
  timeZone,
  selected,
  wrap,
  lineHref,
  onLineLink,
}: {
  line: LogLine;
  matches: LogMatch[];
  currentMatch?: LogMatch;
  highlight?: RegExp;
  timestamps: TimestampMode;
  now: Date;
  timeZone: string;
  selected: boolean;
  wrap: boolean;
  lineHref: string;
  onLineLink: (n: number) => void;
}) {
  const segments = ansiSegments(line.text);
  const visible = segments.map((s) => s.text).join("");
  const ranges: { start: number; end: number; kind: Segment["match"] | "tint" }[] = matches.map((m) => ({
    start: m.start,
    end: m.end,
    kind: currentMatch && m.start === currentMatch.start && m.line === currentMatch.line ? "current" : "match",
  }));
  if (highlight && !ranges.length) {
    for (const m of visible.matchAll(new RegExp(highlight.source, highlight.flags.includes("g") ? highlight.flags : `${highlight.flags}g`))) {
      const group = m[1] ?? m[0];
      const start = (m.index ?? 0) + m[0].indexOf(group);
      ranges.push({ start, end: start + group.length, kind: "tint" });
    }
  }
  const parts = mark(segments, ranges);
  return (
    <div
      id={`L${line.n}`}
      data-line={line.n}
      className={cn(
        "flex gap-4 border-l-2 border-transparent pr-3 pl-1 font-mono text-log leading-[var(--nd-log-line-height)] text-fg-body hover:bg-hover",
        line.stream === "stderr" && "border-danger-dot",
        selected && "bg-selected",
      )}
    >
      <a
        href={lineHref}
        onClick={(e) => {
          e.preventDefault();
          onLineLink(line.n);
        }}
        className="w-8 shrink-0 text-right text-fg-faint select-none hover:text-fg-primary"
        aria-label={`Copy link to line ${line.n}`}
      >
        {line.n}
      </a>
      {timestamps !== "off" && line.timestamp ? (
        <span className="shrink-0 font-sans text-fg-muted tabular-nums select-none" title={formatAbsolute(line.timestamp, timeZone)}>
          {timestamps === "relative" ? formatRelative(line.timestamp, now, timeZone) : formatLogTime(line.timestamp, timeZone)}
        </span>
      ) : null}
      {line.rank !== undefined ? <span className="shrink-0 text-fg-muted">[r{line.rank}]</span> : null}
      {line.index !== undefined ? <span className="shrink-0 text-fg-muted">[i{line.index}]</span> : null}
      <span className={cn("min-w-0", wrap ? "break-all whitespace-pre-wrap" : "whitespace-pre")}>
        {parts.map((p, i) => (
          <span
            key={i}
            style={p.color ? { color: p.color } : undefined}
            className={cn(
              p.bold && "font-medium",
              p.tint && "text-copper-text",
              p.match === "match" && "bg-copper-bg",
              p.match === "current" && "bg-copper-bg outline outline-1 outline-copper-border",
            )}
          >
            {p.text}
          </span>
        ))}
      </span>
    </div>
  );
}
